/**
 * `AIRealtimeTransportModel.ts` — port of Unity's WebSocket realtime
 * transport (`AIAgentTransportWebSocket.cs` + audio partial).
 *
 * Wire protocol: OpenAI Realtime JSON events over
 * `{aiServiceWebSocketOrigin()}/v1/realtime?model={model}` — the model is
 * per-session, resolved from the active role by the session host (stock:
 * `realtimeModel`). The origin is derived rather than built as `wss://{host}`
 * because `aisvc_host` may carry a scheme: a locally run oaiproxy is
 * `http://localhost:3000` and has to stay on plain `ws://`.
 *
 * Auth — a browser cannot set headers on a WebSocket upgrade, so whichever
 * credential applies rides `Sec-WebSocket-Protocol`:
 *   - campfire AI proxy hosts (including a local one) → the signed-in user's
 *     Cognito ID token as `authorization.{token}`, the spelling the proxy and
 *     sioserver both parse. The proxy strips it before the upstream handshake
 *     and selects `realtime` to echo back, so the token reaches neither
 *     OpenAI nor a response header. Sending nothing was correct until
 *     2026-10-03, when the proxy began authenticating the upgrade; an
 *     anonymous one is now refused `401`, which surfaces as `websocket_error`.
 *   - other hosts → mint an ephemeral key and pass it as
 *     `openai-insecure-api-key.{key}`.
 *
 * Audio: mic PCM16 base64 → `input_audio_buffer.append`; agent audio deltas
 * (`response.output_audio.delta` / `response.audio.delta`) are peeled off
 * locally into the playback queue, then every full JSON event is forwarded
 * to the session via `onMessage` (same split as Unity).
 *
 * This is ONE of two `IRealtimeTransport` implementations and the default one;
 * `AIRealtimeTransportModelRTC.ts` is the other. The contract they share — and
 * the things that fail silently if a second implementation gets them wrong —
 * is documented on `IRealtimeTransport.ts`, not here.
 */

import { Emitter } from "@core/events/emitter";
import { getAiConfig, isCampfireAiProxyHost } from "@ai/aiConfig";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import { RealtimeMicCapture, RealtimePcmPlayback } from "./realtimeAudio";
import type {
  IRealtimeTransport,
  RealtimeMicrophoneReason,
} from "./IRealtimeTransport";
import { mintRealtimeEphemeralKeyAsync } from "./realtimeKeyMint";
import { aiServiceWebSocketOrigin } from "@ai/subagents/aiResponsesUtility";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { getVoiceSettings } from "@models/data/voiceSettings";

export class AIRealtimeTransportModel implements IRealtimeTransport {
  readonly onConnected = new Emitter<void>();
  readonly onDisconnected = new Emitter<void>();
  readonly onMessage = new Emitter<string>();
  readonly onError = new Emitter<string>();

  /** Model for the `?model=` URL query — per-role (the session host resolves it per connect). */
  private readonly m_model: string;
  private m_socket: WebSocket | null = null;
  private m_connected = false;
  private readonly m_mic = new RealtimeMicCapture();
  private readonly m_playback = new RealtimePcmPlayback();
  private readonly m_log: IRealtimeConversationLog | null;
  private m_microphoneRecording = false;
  private m_microphoneReason: RealtimeMicrophoneReason = "session_sync";

  constructor(model?: string, log: IRealtimeConversationLog | null = null) {
    this.m_model = model?.trim() || getVoiceSettings().realtime.model;
    this.m_log = log;
  }

  get onAgentSpeakingChanged(): Emitter<boolean> {
    return this.m_playback.onSpeakingChanged;
  }

  get isConnected(): boolean {
    return this.m_connected;
  }

  get isAgentSpeaking(): boolean {
    return this.m_playback.isSpeaking;
  }

  get hasPreciseAgentSpeechCompletion(): boolean {
    // PCM deltas expose queue state, but this transport receives no provider
    // output-buffer stop signal with which to corroborate the local meter.
    return false;
  }

  get micInputLevel(): number {
    return this.m_mic.inputLevel;
  }

  async connectAsync(): Promise<boolean> {
    if (this.m_socket) return this.m_connected;
    const config = getAiConfig();
    const url = `${aiServiceWebSocketOrigin()}/v1/realtime?model=${this.m_model}`;

    let protocols: string[];
    if (isCampfireAiProxyHost(config.aiSvcHost)) {
      // The proxy authenticates the upgrade as of 2026-10-03 and refuses an
      // anonymous one with 401. A browser cannot set headers on a handshake,
      // so the token rides the subprotocol list; the proxy strips it before
      // the upstream handshake and selects `realtime` to echo back, so the
      // credential reaches neither OpenAI nor the response headers.
      const idToken = await Ioc.resolve<IAuthModel>(AUTH_MODEL).getIdToken(false);
      if (!idToken) {
        this.onError.emit("realtime_auth_unavailable");
        return false;
      }
      protocols = ["realtime", `authorization.${idToken}`];
    } else {
      const ephemeralKey = await mintRealtimeEphemeralKeyAsync();
      if (!ephemeralKey) {
        this.onError.emit("realtime_key_mint_failed");
        return false;
      }
      // No `openai-beta.realtime-v1`: GA guidance says to drop the beta marker,
      // and Unity — the parity reference — never sent it either.
      protocols = ["realtime", `openai-insecure-api-key.${ephemeralKey}`];
    }

    return await new Promise<boolean>((resolve) => {
      let settled = false;
      const socket = new WebSocket(url, protocols);
      this.m_socket = socket;

      socket.onopen = () => {
        this.m_connected = true;
        // Mic capture (and its microphone-permission prompt) is deferred until
        // the first unmute — see setMuted. Connecting must not touch getUserMedia.
        this.onConnected.emit();
        if (!settled) {
          settled = true;
          resolve(true);
        }
      };
      socket.onmessage = (event: MessageEvent) => {
        if (typeof event.data !== "string") return;
        this.peelAudioDelta(event.data);
        this.onMessage.emit(event.data);
      };
      socket.onerror = () => {
        this.onError.emit("websocket_error");
        if (!settled) {
          settled = true;
          resolve(false);
        }
      };
      socket.onclose = () => {
        const wasConnected = this.m_connected;
        this.m_connected = false;
        this.m_socket = null;
        this.setMicrophoneRecording(false, "session_sync");
        this.m_mic.stop();
        if (wasConnected) this.onDisconnected.emit();
        if (!settled) {
          settled = true;
          resolve(false);
        }
      };
    });
  }

  disconnect(): void {
    this.setMicrophoneRecording(false, "session_sync");
    this.m_mic.stop();
    this.m_playback.stopPlayback();
    this.m_socket?.close();
    this.m_socket = null;
    this.m_connected = false;
  }

  sendEvent(json: string): void {
    if (!this.m_socket || this.m_socket.readyState !== WebSocket.OPEN) return;
    this.m_socket.send(json);
  }

  setMuted(
    muted: boolean,
    reason: RealtimeMicrophoneReason = "session_sync",
  ): void {
    this.m_microphoneReason = reason;
    if (!muted && !this.m_mic.isCapturing) {
      // First unmute (e.g. first Spacebar hold): request the microphone now,
      // so the browser permission prompt appears only when the mic is used —
      // never on page load or session connect.
      void this.m_mic
        .startAsync((base64) => {
          this.sendEvent(
            JSON.stringify({ type: "input_audio_buffer.append", audio: base64 }),
          );
        })
        .then((started) => {
          if (started && this.m_connected && !this.m_mic.isMuted) {
            this.setMicrophoneRecording(true, this.m_microphoneReason);
          }
        });
    }
    this.m_mic.setMuted(muted);
    if (muted) {
      this.setMicrophoneRecording(false, reason);
    } else if (this.m_mic.isCapturing) {
      this.setMicrophoneRecording(true, reason);
    }
    if (!muted) this.m_playback.resumeIfNeeded();
  }

  resumeAudioOutputIfNeeded(): void {
    this.m_playback.resumeIfNeeded();
  }

  stopOutputAudioPlayback(): void {
    this.m_playback.stopPlayback();
  }

  dispose(): void {
    this.disconnect();
    this.m_playback.dispose();
  }

  private setMicrophoneRecording(
    recording: boolean,
    reason: RealtimeMicrophoneReason,
  ): void {
    if (this.m_microphoneRecording === recording) return;
    this.m_microphoneRecording = recording;
    const scope = reason === "push_to_talk" ? "ptt" : "microphone";
    this.m_log?.note(
      `${scope}.recording.${recording ? "started" : "stopped"}`,
      `reason=${reason}, transport=websocket`,
      { recording, reason, transport: "websocket" },
    );
  }

  /** Decode agent audio deltas locally (Unity peels them in the transport). */
  private peelAudioDelta(json: string): void {
    // Fast-path check before paying for a JSON parse on every event.
    if (!json.includes('"response.audio.delta"') && !json.includes('"response.output_audio.delta"')) {
      return;
    }
    try {
      const event = JSON.parse(json) as { type?: string; delta?: string };
      if (
        (event.type === "response.audio.delta" || event.type === "response.output_audio.delta") &&
        typeof event.delta === "string"
      ) {
        this.m_playback.enqueueBase64Pcm16(event.delta);
      }
    } catch {
      // Malformed frames are surfaced by the session's own parse handling.
    }
  }

}
